import { FC, RefObject, useCallback, useMemo, useRef } from 'react';
import { Form, useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import {
  Button,
  Divider,
  Option,
  Typography,
} from '@beeline/design-system-react/extracted-styles';

import { OrganizationInfo } from 'src/api/types';
import { FloorMapProvider } from 'src/components/FloorMap/FloorMapProvider';
import { toFloor } from 'src/components/FloorSelect/FloorSelect';
import { FormPersonAutocomplete } from 'src/components/FormPersonAutocomplete/FormPersonAutocomplete';
import { FormTextField } from 'src/components/FormTextField/FormTextField';
import { Modal } from 'src/components/Modal/Modal';
import { FormOrganizationAutocomplete } from 'src/components/OrganizationAutocomplete/FormOrganizationAutocomplete';
import { toPersonOption } from 'src/components/PersonAutocomplete/PersonAutocomplete';
import { getIsMobile } from 'src/store/agent/selectors';
import { getSpacesState } from 'src/store/spaces/selectors';
import { spacesActions } from 'src/store/spaces/slice';
import { MapInfo } from 'src/store/spaces/types';
import { useAppDispatch } from 'src/store/useAppDispatch';

import classes from './SpaceDialog.module.scss';
import { SpaceDialogFormValues, useSpaceDialog } from './SpaceDialogProvider';
import { SpaceFloorMap } from './SpaceFloorMap/SpaceFloorMap';
import { SpaceFloorMapSettings } from './SpaceFloorMap/SpaceFloorMapSettings/SpaceFloorMapSettings';
import { SpaceGroups } from './SpaceGroups/SpaceGroups';
import { SpaceBookingObjectOption } from './types';

const emptyValues: SpaceDialogFormValues = {
  id: undefined,
  title: '',
  employee: null,
  organization: null,
  groups: new Map<number, SpaceBookingObjectOption[]>(),
  groupInfos: new Map<number, MapInfo>(),
  administrator: null,
};

interface InnerProps {
  containerRef: RefObject<HTMLDivElement>;
}

const Inner: FC<InnerProps> = ({ containerRef }) => {
  const { space } = useSelector(getSpacesState);
  const { change } = useForm();
  const employeeNumber = useField<Option<string> | null>('employee').input.value
    ?.id as string | undefined;

  const handleChangeEmployee = useCallback(() => {
    change('organization', null);
  }, [change]);

  return (
    <div className={classes.container} ref={containerRef}>
      <div className={classes.fullWidth}>
        <Typography variant='h4'>
          {space ? 'Редактирование пространства' : 'Создание пространства'}
        </Typography>
      </div>
      <div className={classes.title}>
        <FormTextField
          name='title'
          size='medium'
          label='Наименование'
          fullWidth
          dataTestId='spaceTitle'
          required
        />
      </div>
      <div className={classes.administrator}>
        <FormPersonAutocomplete
          name='administrator'
          type='search'
          dataTestId='administrator'
          label='Администратор пространства'
        />
      </div>
      <div className={classes.employee}>
        <FormPersonAutocomplete
          name='employee'
          type='search'
          dataTestId='employee'
          onChange={handleChangeEmployee}
          label='Сотрудник'
        />
      </div>
      <div className={classes.organization}>
        <FormOrganizationAutocomplete
          name='organization'
          employeeNumber={employeeNumber}
          required
        />
      </div>
      <div className={classes.fullWidth}>
        <Divider />
      </div>

      <div className={classes.controls}>
        <SpaceFloorMapSettings containerRef={containerRef} />
      </div>
      <div className={classes.left}>
        <SpaceFloorMap />
      </div>
      <div className={classes.right}>
        <SpaceGroups />
      </div>
    </div>
  );
};

const Dialog: FC = () => {
  const isMobile = useSelector(getIsMobile);
  const dispatch = useAppDispatch();

  const { floor, backgroundData, spaceDialogIsOpen, space } =
    useSelector(getSpacesState);

  const close = useCallback(() => {
    dispatch(spacesActions.closeSpace());
  }, [dispatch]);

  const { submit } = useForm();

  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <Modal
      open={spaceDialogIsOpen}
      onClose={close}
      title=''
      actions={
        <Button
          variant='contained'
          data-testid='submitSpace'
          onClick={submit}
          fullWidth={isMobile}
        >
          {space ? 'Сохранить' : 'Создать'}
        </Button>
      }
      ref={containerRef}
    >
      <FloorMapProvider floor={toFloor(floor)} backgroundData={backgroundData}>
        <Inner containerRef={containerRef} />
      </FloorMapProvider>
    </Modal>
  );
};

export const SpaceDialog: FC = () => {
  const { save, validateSpaceDialog } = useSpaceDialog();

  const { space, spaceDialogIsOpen } = useSelector(getSpacesState);

  const initialValues = useMemo((): SpaceDialogFormValues | null => {
    if (!space) return emptyValues;

    const organization = space.organizations[0] as OrganizationInfo | undefined;
    const groups = new Map<number, SpaceBookingObjectOption[]>();
    const groupInfos = new Map<number, MapInfo>();
    for (const place of space.places) {
      const group: SpaceBookingObjectOption[] = groups.get(place.floorId) ?? [];
      group.push({
        id: place.placeId,
        value: { title: place.title ?? 'Рабочее место', space: null },
      });
      groups.set(place.floorId, group);
    }
    for (const relatedFloor of space.relatedFloors) {
      groupInfos.set(relatedFloor.floorId, relatedFloor);
    }

    return {
      id: space.spaceId,
      title: space.title,
      organization: organization
        ? {
            id: organization.id,
            value: organization,
          }
        : null,
      employee: null,
      groups,
      groupInfos,
      administrator: toPersonOption(space.administrator),
    };
  }, [space]);

  return spaceDialogIsOpen ? (
    <>
      <Form onSubmit={save} initialValues={initialValues}>
        {() => <Dialog />}
      </Form>
      {validateSpaceDialog}
    </>
  ) : null;
};
